import React, { useCallback, useEffect, useState } from 'react';
import {
  LocationSkillConfig,
  SuburbLocation,
  SuburbLocationService,
} from '../services/suburbLocationService';

export const LocationReferenceManagerPanel: React.FC = () => {
  const [config, setConfig] = useState<LocationSkillConfig | null>(null);
  const [suburbs, setSuburbs] = useState<SuburbLocation[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<SuburbLocation>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadConfig = useCallback(async () => {
    setConfig(await SuburbLocationService.getLocationConfig());
  }, []);

  const loadSuburbs = useCallback(async (query: string) => {
    const result = await SuburbLocationService.searchSuburbs(query, 50, 0);
    setSuburbs(result.suburbs);
    setTotal(result.total);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      let suburbLoadError: string | null = null;

      try {
        await loadSuburbs('');
      } catch (err: unknown) {
        suburbLoadError =
          err instanceof Error ? err.message : 'Failed to load suburb reference data';
        setError(suburbLoadError);
      }

      try {
        await loadConfig();
      } catch (err: unknown) {
        setConfig(null);
        if (!suburbLoadError) {
          setError(err instanceof Error ? err.message : 'Failed to load location enum config');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [loadConfig, loadSuburbs]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      await loadSuburbs(search);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Search failed');
    }
  };

  const suburbKey = (suburb: SuburbLocation) =>
    `${suburb.name}::${suburb.territorial_authority}`;

  const handleSaveSuburb = async (suburb: SuburbLocation) => {
    await SuburbLocationService.updateSuburb(suburb.name, suburb.territorial_authority, {
      lat: draft.lat,
      lng: draft.lng,
      is_legacy: draft.is_legacy,
      major_name: draft.major_name ?? undefined,
    });
    setEditingKey(null);
    setDraft({});
    await loadSuburbs(search);
  };

  if (loading && !config) {
    return <div className="referrer-type-panel loading">Loading location reference data…</div>;
  }

  return (
    <div className="referrer-type-panel">
      {error && <div className="error-banner">{error}</div>}

      {config && (
        <section className="referrer-type-section skill-enum-section">
          <div className="section-heading-row">
            <h3>Extraction output enums</h3>
            <span className="tier-score">Pipeline values</span>
          </div>
          <p className="section-help">
            Allowed values written by the location extraction and clinic routing pipeline.
          </p>
          <div className="enum-chip-grid">
            <div>
              <strong>Status</strong>
              <div className="enum-chips">
                {config.status_values.map((v) => (
                  <span key={v} className="enum-chip">{v}</span>
                ))}
              </div>
            </div>
            <div>
              <strong>Source</strong>
              <div className="enum-chips">
                {config.source_values.map((v) => (
                  <span key={v} className="enum-chip">{v}</span>
                ))}
              </div>
            </div>
            <div>
              <strong>Confidence</strong>
              <div className="enum-chips">
                {config.confidence_values.map((v) => (
                  <span key={v} className="enum-chip">{v}</span>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="referrer-type-section skill-enum-section">
        <div className="section-heading-row">
          <h3>Suburb geocoding reference</h3>
          <span className="tier-score">{total.toLocaleString()} suburbs</span>
        </div>
        <p className="section-help">
          LINZ suburb/locality rows used for patient suburb geocoding. Edit coordinates or legacy flag for corrections.
        </p>

        <form className="referrer-type-toolbar" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Search suburb, city, or council…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn btn-primary">Search</button>
        </form>

        <div className="referrer-type-table-wrap">
          <table className="referrer-type-table">
            <thead>
              <tr>
                <th>Suburb</th>
                <th>Major name</th>
                <th>Council</th>
                <th>Lat</th>
                <th>Lng</th>
                <th>Legacy</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {suburbs.map((suburb) => {
                const key = suburbKey(suburb);
                const editing = editingKey === key;
                return (
                  <tr key={key}>
                    <td>{suburb.name}</td>
                    <td>
                      {editing ? (
                        <input
                          value={draft.major_name ?? suburb.major_name ?? ''}
                          onChange={(e) => setDraft((d) => ({ ...d, major_name: e.target.value }))}
                        />
                      ) : (
                        suburb.major_name || '—'
                      )}
                    </td>
                    <td>{suburb.territorial_authority}</td>
                    <td>
                      {editing ? (
                        <input
                          type="number"
                          step="0.000001"
                          value={draft.lat ?? suburb.lat}
                          onChange={(e) => setDraft((d) => ({ ...d, lat: Number(e.target.value) }))}
                          className="input-narrow"
                        />
                      ) : (
                        suburb.lat
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          type="number"
                          step="0.000001"
                          value={draft.lng ?? suburb.lng}
                          onChange={(e) => setDraft((d) => ({ ...d, lng: Number(e.target.value) }))}
                          className="input-narrow"
                        />
                      ) : (
                        suburb.lng
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          type="checkbox"
                          checked={draft.is_legacy ?? suburb.is_legacy}
                          onChange={(e) => setDraft((d) => ({ ...d, is_legacy: e.target.checked }))}
                        />
                      ) : (
                        suburb.is_legacy ? 'Yes' : 'No'
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <>
                          <button type="button" className="btn btn-link" onClick={() => handleSaveSuburb(suburb)}>
                            Save
                          </button>
                          <button
                            type="button"
                            className="btn btn-link"
                            onClick={() => {
                              setEditingKey(null);
                              setDraft({});
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-link"
                          onClick={() => {
                            setEditingKey(key);
                            setDraft({ ...suburb });
                          }}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
